"use client";
import { IconCookieConsent } from "@/lib/images";
import { BarChart3, Shield, Target, X } from "lucide-react";
import Image from "next/image";
import { useState } from "react";

interface CookieConsentDialogProps {
    isOpen: boolean;
    onAccept: () => void;
    onReject: () => void;
    onClose: () => void;
}

export default function CookieConsentDialog({
    isOpen,
    onAccept,
    onReject,
    onClose,
}: CookieConsentDialogProps) {
    const [showDetails, setShowDetails] = useState(false);

    if (!isOpen) return null;

    return (
        <div
            role="dialog"
            aria-labelledby="cookie-consent-title"
            aria-describedby="cookie-consent-description"
            className="fixed bottom-4 left-4 z-50 w-[calc(100vw-2rem)] max-w-md animate-in slide-in-from-bottom-4 fade-in duration-300"
        >
            <div className="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 p-4 pb-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 shrink-0 bg-orange-100 rounded-lg flex items-center justify-center">
                            <Image
                                src={IconCookieConsent}
                                alt=""
                                className="w-4 h-4"
                            />
                        </div>
                        <div className="min-w-0">
                            <h2
                                id="cookie-consent-title"
                                className="text-base font-medium text-gray-900"
                            >
                                Cookie Preferences
                            </h2>
                            <p className="text-xs text-gray-500">
                                Help us improve your experience
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Dismiss cookie notice"
                        className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-1 -m-1"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 pt-3">
                    <p
                        id="cookie-consent-description"
                        className="text-sm text-gray-600 leading-relaxed mb-3"
                    >
                        We use cookies to make our website work better for you. Some are essential
                        for the site to function, while others help us understand how you use our
                        site so we can improve it.
                    </p>

                    {/* Cookie Types */}
                    {showDetails && (
                        <div className="mb-3 space-y-2">
                            <div className="flex items-start gap-2.5 p-2.5 bg-gray-50 rounded-md">
                                <Shield className="w-4 h-4 text-gray-600 mt-0.5 shrink-0" />
                                <div>
                                    <h4 className="font-medium text-gray-900 text-sm">Essential</h4>
                                    <p className="text-xs text-gray-500">Required for the site to work</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-2.5 p-2.5 bg-gray-50 rounded-md">
                                <BarChart3 className="w-4 h-4 text-gray-600 mt-0.5 shrink-0" />
                                <div>
                                    <h4 className="font-medium text-gray-900 text-sm">Analytics</h4>
                                    <p className="text-xs text-gray-500">Help us improve the site</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-2.5 p-2.5 bg-gray-50 rounded-md">
                                <Target className="w-4 h-4 text-gray-600 mt-0.5 shrink-0" />
                                <div>
                                    <h4 className="font-medium text-gray-900 text-sm">Marketing</h4>
                                    <p className="text-xs text-gray-500">Show relevant content</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={() => setShowDetails(!showDetails)}
                        className="text-primary hover:text-primary/80 text-sm font-medium transition-colors mb-3"
                    >
                        {showDetails ? "Show less" : "Learn more"}
                    </button>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={onReject}
                            className="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-full transition-colors duration-200"
                        >
                            Decline
                        </button>

                        <button
                            type="button"
                            onClick={onAccept}
                            className="flex-1 px-3 py-2 text-sm bg-black hover:bg-gray-800 text-white font-medium rounded-full transition-colors duration-200"
                        >
                            Accept
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
